/**
 * Semantic GRAPH validation (D61) — the §14 checks that ask whether the
 * world's shape can be played, not merely whether its ids resolve.
 *
 * @remarks
 * The third validation layer, and the one the docs deferred "until the phases
 * that give them meaning": references (`semantic-validation.ts`) prove every
 * id names something; reachability rules (`authoring-reachability.ts`) prove
 * authored content can be SEEN; this module proves the world can be WALKED.
 *
 * Two rules, both of the same species as D31/D47's dead-content law — a
 * defect here is not a crash but a game that quietly cannot be finished:
 *
 * - **Every room is reachable from the entry.** An unreachable room is a
 *   whole subtree of authored content nothing can visit. The walk ignores
 *   locks on purpose: a locked exit is a puzzle, not a wall, and the second
 *   rule owns whether the puzzle can actually be solved.
 * - **Every lock is openable.** The v0 lock semantics (rooms module, R1.1):
 *   `unlockedExitIds` — fed only by `unlock_exit` effects — always wins;
 *   otherwise a `lockedFlag` reading `false` opens the exit; a lock with no
 *   flag stays shut forever unless something unlocks it. So a lock is DEAD
 *   unless some interaction fires `unlock_exit` at its exit, or its
 *   `lockedFlag` either starts `false` or is written `false` by some effect.
 *   A dead lock walls off everything behind it with a refusal narrative that
 *   reads like a puzzle and is actually the edge of the world.
 *
 * Deliberately NOT here, and where it lives instead: whether a puzzle is
 * REQUIRED (as opposed to solvable) is a promise about routes-around, owned
 * by the bypass suite (D53); whether an optional secret stays optional is the
 * same species and lives there too. Softlock analysis (a solvable world made
 * unsolvable by spending a consumable in the wrong place) needs effect
 * SIMULATION rather than effect existence, and arrives with the phase that
 * makes effects consume things.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, ContentPackDefinition, EffectDefinition, ExitLock } from '@orpheus/content-schema';

import { findConstantBinaryStateCondition } from './authoring-reachability.js';
import { everyEffectOf } from './effect-sources.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference } from './semantic-validation.js';

/**
 * Run the graph checks against a schema-valid, reference-valid pack.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per unreachable room and per dead lock, all collected
 */
export const validateWorldGraph = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	/*
	 * EVERY effect source counts (R11, fixing an R9 gap): an `unlock_exit`
	 * fired by a scheduled event or a standing trigger opens the lock exactly
	 * as an interaction's does — judging locks by interaction effects alone
	 * flagged time-opened doors as dead.
	 */
	const everyEffect = everyEffectOf(contentPack);

	failures.push(...unreachableRoomFailures(contentPack));

	const itemsById = new Map(contentPack.items.map((item) => [item.itemId as string, item]));

	contentPack.rooms.forEach((room, roomIndex) => {
		room.exits.forEach((exit, exitIndex) => {
			// D138: a reveal whose condition is constant by structure is an exit
			// that was never hidden, or one that never appears — dead content
			// either way (the sprite `visibleWhen` precedent).
			const constantReveal = exit.reveal === undefined ? undefined : findConstantBinaryStateCondition(exit.reveal.when, itemsById);

			if (constantReveal !== undefined) {
				failures.push(
					brokenReference(
						`rooms[${String(roomIndex)}].exits[${String(exitIndex)}].reveal.when`,
						exit.exitId,
						constantReveal.type === 'switched_on'
							? 'that thing declares no switch, so this exit can never be revealed'
							: 'that thing declares no door, so this reveal is constant — an exit that is always there should not declare one',
					),
				);
			}

			/*
			 * `leadsOutOfPack` exits are exempt on purpose: they are already the
			 * edge of the world (D19 — every slice has a one-way door behind it),
			 * so a permanent lock there is authored FICTION, not a dead puzzle.
			 * The POC's stairwell door — "no handle on this side" — is the
			 * genre's founding move, and the gatehouse demo locks all three of
			 * its outward exits the same way. The rule guards routes INSIDE the
			 * world, where a lock that never opens walls off authored rooms.
			 */
			if (
				exit.lock !== undefined &&
				exit.leadsOutOfPack === undefined &&
				!isLockOpenable(exit.exitId, exit.lock, contentPack, everyEffect)
			) {
				failures.push(
					brokenReference(
						`rooms[${String(roomIndex)}].exits[${String(exitIndex)}].lock`,
						exit.exitId,
						'this lock can never open — no unlock_exit effect targets the exit and its flag is never written false, so the refusal narrative is the edge of the world wearing a puzzle’s clothes',
					),
				);
			}
		});
	});

	return failures;
};

/**
 * Every declared room the exit graph cannot reach from the entry, as failures.
 *
 * @remarks
 * A breadth-first walk over `targetRoomId` edges, locks ignored (see the
 * module remarks). `leadsOutOfPack` exits contribute no edge — leaving the
 * slice is an ending, not a route.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per unreachable room, in declaration order
 */
const unreachableRoomFailures = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const exitsByRoomId = new Map(contentPack.rooms.map((room) => [room.roomId as string, room.exits]));
	const reachedRoomIds = new Set<string>([contentPack.manifest.entryRoomId]);
	const frontier: string[] = [contentPack.manifest.entryRoomId];

	// `for-of` over an array that grows while iterated is the BFS: the iterator
	// sees appended rooms, and appending only unseen ids guarantees termination.
	for (const roomId of frontier) {
		for (const exit of exitsByRoomId.get(roomId) ?? []) {
			if (exit.targetRoomId !== undefined && !reachedRoomIds.has(exit.targetRoomId)) {
				reachedRoomIds.add(exit.targetRoomId);
				frontier.push(exit.targetRoomId);
			}
		}
	}

	return contentPack.rooms.flatMap((room, roomIndex) =>
		reachedRoomIds.has(room.roomId)
			? []
			: [
				brokenReference(
					`rooms[${String(roomIndex)}].roomId`,
					room.roomId,
					'no path of exits reaches this room from the entry — everything authored in it is content nothing can visit',
				),
			],
	);
};

/**
 * Can this lock ever open, under the v0 semantics?
 *
 * @param exitId - The locked exit
 * @param lock - The lock itself — its bolt, and (D233) whether it has one
 * @param contentPack - The parsed pack (flag declarations)
 * @param everyEffect - Every effect any interaction can fire
 * @returns Whether some authored route opens it
 */
const isLockOpenable = (
	exitId: string,
	lock: ExitLock,
	contentPack: ContentPackDefinition,
	everyEffect: readonly EffectDefinition[],
): boolean => {
	const { lockedWhen } = lock;
	const hasUnlockEffect = everyEffect.some((effect) => effect.type === 'unlock_exit' && effect.exitId === exitId);

	if (hasUnlockEffect) {
		return true;
	}

	// D233: a lock that declares only an OBSTRUCTION has no bolt — it opens the
	// moment the way is clear. Whether the obstruction can ever clear is the
	// producibility walk's question (an `actor_present` on a body that can
	// move is live), not this gate's: this gate catches the bolt nothing cuts.
	if (lockedWhen === undefined) {
		return lock.blockedWhen !== undefined;
	}

	/*
	 * D158 generalized the old proof rather than replacing it. Under
	 * `lockedFlag` the question was answerable exactly: a lock opened while
	 * its flag read FALSE, so it was dead iff the flag started true and
	 * nothing ever wrote it false. That proof survives for the same shape —
	 * a BARE `flag_equals` leaf — and it is still the overwhelmingly common
	 * lock, so it stays exact where it was exact.
	 *
	 * For a compound condition this claims nothing and says so: deciding
	 * whether `all_of` / `not` / `has_item` can ever go false is
	 * satisfiability, and a validator that guessed at it would refuse honest
	 * packs. Those are treated as openable — the gate is here to catch the
	 * accident of walling off authored rooms, not to be a solver.
	 */
	return lockedWhen.type === 'flag_equals' ? isFlagLockOpenable(lockedWhen, contentPack, everyEffect) : true;
};

/**
 * Whether a bare `flag_equals` lock can ever read open.
 *
 * @param lockedWhen - The lock's flag condition
 * @param contentPack - The parsed pack
 * @param everyEffect - Every effect the pack declares, from any source
 * @returns Whether the flag can ever take the value that opens it
 */
const isFlagLockOpenable = (
	lockedWhen: Extract<ConditionDefinition, { readonly type: 'flag_equals' }>,
	contentPack: ContentPackDefinition,
	everyEffect: readonly EffectDefinition[],
): boolean => {
	// The lock opens when the flag does NOT hold the value that locks it:
	// either it starts there (the lock begins open and something may close it
	// later), or some effect writes it there.
	const isOpenValue = !lockedWhen.expectedValue;
	const isInitiallyUnlocked = contentPack.flags.some(
		(flag) => flag.flagId === lockedWhen.flagId && flag.initialValue === isOpenValue,
	);

	return (
		isInitiallyUnlocked ||
		everyEffect.some((effect) => effect.type === 'set_flag' && effect.flagId === lockedWhen.flagId && effect.value === isOpenValue)
	);
};
